import { createApi } from "@reduxjs/toolkit/query/react";
import { pyrotchBaseUrl, axiosBaseQuery } from "../axios/AxiosInstance";
import {
  DataItemArray,
  ProjectMilestoneArray,
  Milestone,
} from "../../models/Milestone";

export const MileStoneApi = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: pyrotchBaseUrl + "v1/",
  }),
  reducerPath: "milestoneApi",
  tagTypes: ["Milestone"],
  endpoints: (build) => ({
    getMilestoneData: build.query<DataItemArray, void>({
      query: () => {
        return {
          url: "project-milestone",
          method: "GET",
        };
      },
    }),

    getProjectMilestoneData: build.mutation<ProjectMilestoneArray, number>({
      query: (projectId) => {
        return {
          url: `project/${projectId}/list-view`,
          method: "GET",
        };
      },
    }),

    getProjectMilestoneData1: build.query<ProjectMilestoneArray, number>({
      query: (projectId) => {
        return {
          url: `project/${projectId}/list-view`,
          method: "GET",
        };
      },
      providesTags: ["Milestone"],
    }),

    addedMilestones: build.mutation<
      any,
      { projectId: number; milestones: Milestone }
    >({
      query: ({ projectId, milestones }) => {
        return {
          url: `project/${projectId}/assign-milestones`,
          method: "POST",
          data: milestones,
        };
      },
      invalidatesTags: ["Milestone"],
    }),

    updateMilestone: build.mutation<
      any,
      {
        projectId: number;
        milestoneId: number;
        milestoneName: string;
        startDate: string;
        endDate: string;
        description: string;
        userIds: any;
      }
    >({
      query: ({
        projectId,
        milestoneId,
        milestoneName,
        startDate,
        endDate,
        description,
        userIds,
      }) => {
        return {
          url: `project/${projectId}/update-milestone`,
          method: "POST",
          data: {
            milestoneId,
            milestoneName,
            startDate,
            endDate,
            description,
            userIds,
          },
        };
      },
    }),

    deleteMilestone: build.mutation<
      any,
      { projectId: number; milestoneId: number }
    >({
      query: ({ projectId, milestoneId }) => {
        return {
          url: `project/${projectId}/delete-milestone`,
          method: "DELETE",
          params: { projectId, milestoneId },
        };
      },
    }),

    getProjectOTD: build.query<any, { projectId: number }>({
      query: ({ projectId }) => {
        return {
          url: `project/${projectId}/project-otd`,
          method: "GET",
        };
      },
    }),

    milestoneRequestApproval: build.mutation<
      any,
      {
        projectId: number;
        milestoneId: number;
        comment: string;
        files: File[];
      }
    >({
      query: ({ projectId, milestoneId, comment, files }) => {
        const formData = new FormData();

        // Append other fields as string parameters in the body (as JSON-like fields)
        // formData.append("projectId", projectId.toString());
        // formData.append("milestoneId", milestoneId.toString());
        // formData.append("comment", comment);

        // Append files as binary parts (as file objects)
        files.forEach((file) => {
          formData.append("files", file); // 'files' part should receive the actual file
        });

        const body = {
          projectId,
          milestoneId,
          comment,
        };

        formData.append(
          "request",
          JSON.stringify({
            projectId,
            milestoneId,
            comment,
          })
        );

        return {
          url: `project/${projectId}/milestone/${milestoneId}/request-approval`, // Do not append files as query params, only send them in FormData
          method: "POST",
          data: formData,
          params: body,
          headers: {
            "Content-Type": "multipart/form-data", // Required to send FormData
          },
        };
      },
    }),

    milestoneApproveDecline: build.mutation<
      any,
      {
        projectId: number;
        milestoneId: number;
        isApprove: boolean;
        declineComment: string;
      }
    >({
      query: ({ projectId, milestoneId, isApprove, declineComment }) => {
        return {
          url: `project/milestone/approve-decline`,
          method: "POST",
          data: { projectId, milestoneId, isApprove, declineComment },
        };
      },
      invalidatesTags: ["Milestone"],
    }),
  }),
});

export const {
  useGetMilestoneDataQuery,
  useGetProjectMilestoneDataMutation,
  useGetProjectMilestoneData1Query,
  useAddedMilestonesMutation,
  useUpdateMilestoneMutation,
  useDeleteMilestoneMutation,
  useMilestoneRequestApprovalMutation,
  useMilestoneApproveDeclineMutation,
  useLazyGetProjectOTDQuery,
  useGetProjectOTDQuery,
} = MileStoneApi;
